import { useState, useEffect, useRef } from "react";
import { useSectionProgress } from "@/hooks/useSectionProgress";
import Reveal from "@/components/site/Reveal";
import { useState as useTabState } from "react";
import { Lightbulb, Drill, Syringe, Waves, Droplets, Gauge, Armchair, Flame, Radiation, Wind, Zap } from "lucide-react";

/*
 * Showcase del sillón con FOTOS REALES, estilo Apple:
 * una secuencia de fotogramas (video de un sillón real orbitado con la cámara,
 * de abajo hacia arriba y de un lado al otro) que se "scrubbea" con el scroll
 * sobre un canvas sticky. 3D real percibido, sin librerías 3D.
 *
 * Los fotogramas viven en frontend/public/sillon/:
 *   - manifest.json  → {"count": 96, "prefix": "frame-", "ext": "webp", "pad": 4}
 *   - frame-0001.webp … frame-0096.webp (de un video real: ver docs/SILLON-3D.md)
 *
 * Si el manifest no existe todavía, se muestra un respaldo limpio (tarjetas de
 * los 7 puntos de servicio) — sin ilustraciones dibujadas.
 */

const PARTS = [
  { icon: Lightbulb, t: "Lámpara LED", d: "Brazo, foco y regulación de intensidad." },
  { icon: Drill, t: "Turbina y micromotor", d: "Piezas de mano: orings, sellos y reemplazo." },
  { icon: Syringe, t: "Jeringa triple", d: "Puntas, válvulas de aire/agua y fugas." },
  { icon: Waves, t: "Scaler ultrasónico", d: "Conector rápido, transductor y calibración." },
  { icon: Droplets, t: "Salivero y succión", d: "Descarga, mangueras corrugadas y desagüe." },
  { icon: Gauge, t: "Sistema hidroneumático", d: "Presiones de aire/agua y válvulas on/off." },
  { icon: Armchair, t: "Tapiz y motores", d: "Respaldo, reclinación y block de control." },
];

function useManifest() {
  const [manifest, setManifest] = useState(undefined); // undefined=cargando, null=no hay
  useEffect(() => {
    fetch("/sillon/manifest.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => setManifest(m && m.count > 1 ? m : null))
      .catch(() => setManifest(null));
  }, []);
  return manifest;
}

function OrbitScrub({ manifest }) {
  const [ref, p] = useSectionProgress();
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const lastDrawn = useRef(-1);
  const { count, prefix = "frame-", ext = "webp", pad = 4 } = manifest;

  const src = (i) => `/sillon/${prefix}${String(i + 1).padStart(pad, "0")}.${ext}`;

  // Precarga progresiva: primero 1 de cada 8, luego el resto.
  useEffect(() => {
    imagesRef.current = new Array(count);
    let cancelled = false;
    const loadOne = (i) => new Promise((res) => {
      const im = new Image();
      im.onload = () => { if (!cancelled) imagesRef.current[i] = im; res(); };
      im.onerror = res;
      im.src = src(i);
    });
    (async () => {
      for (let step = 8; step >= 1; step = step === 8 ? 2 : step - 1) {
        const batch = [];
        for (let i = 0; i < count; i += step) if (!imagesRef.current[i]) batch.push(loadOne(i));
        await Promise.all(batch);
        if (cancelled) return;
        lastDrawn.current = -1; // forzar redibujo con mejor frame
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // Dibujo del frame según progreso
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const idx = Math.min(count - 1, Math.max(0, Math.round(p * (count - 1))));
    // usar el frame cargado más cercano
    let best = null, bestDist = 1e9;
    for (let i = 0; i < count; i++) {
      const im = imagesRef.current[i];
      if (im && Math.abs(i - idx) < bestDist) { best = im; bestDist = Math.abs(i - idx); }
      if (bestDist === 0) break;
    }
    if (!best || lastDrawn.current === idx + bestDist * 1000) return;
    lastDrawn.current = idx + bestDist * 1000;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    // cover
    const s = Math.max(w / best.width, h / best.height);
    const dw = best.width * s, dh = best.height * s;
    ctx.drawImage(best, (w - dw) / 2, (h - dh) / 2, dw, dh);
  }, [p, count]);

  const active = Math.min(PARTS.length - 1, Math.floor(p * PARTS.length));

  return (
    <section ref={ref} style={{ height: "320vh" }} className="relative bg-[#0B1020]" data-testid="sillon-orbit">
      <div className="sticky top-0 h-screen overflow-hidden">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-[#0B1020]/85 to-transparent pt-10 pb-16 px-6 text-center">
          <p className="text-[11px] md:text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-3">
            Conócelo en 360°
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-white">
            Conocemos tu sillón <span className="text-[#0FB4B7]">pieza por pieza</span>
          </h2>
        </div>
        {/* tarjeta de la pieza activa */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1020]/90 to-transparent pb-8 pt-16 px-6">
          <div className="max-w-md mx-auto rounded-2xl border border-[#0FB4B7]/30 bg-[#0e1630]/90 backdrop-blur px-5 py-4 flex items-center gap-3">
            {(() => { const P = PARTS[active].icon; return (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0FB4B7]/15 flex-shrink-0">
                <P className="h-5 w-5 text-[#0FB4B7]" />
              </span> ); })()}
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-white leading-tight">{PARTS[active].t}</p>
              <p className="text-[12.5px] text-white/55 mt-0.5 leading-snug">{PARTS[active].d}</p>
            </div>
            <span className="ml-auto text-[11px] text-white/40 font-semibold flex-shrink-0">{active + 1}/{PARTS.length}</span>
          </div>
          <div className="max-w-xs mx-auto h-1 mt-4 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#0FB4B7]" style={{ width: `${Math.round(p * 100)}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
}

const EQUIPOS_DETALLE = [
  { id: "sillon", icon: Armchair, nombre: "Sillón dental", piezas: [
    ["Lámpara LED", "Brazo articulado, foco, mica y regulación de intensidad"],
    ["Turbina", "Rodamientos, orings del acople y spray de refrigeración"],
    ["Micromotor", "Escobillas/embobinado, acople y mangueras midwest"],
    ["Jeringa triple", "Puntas, botones, orings y válvulas de aire/agua"],
    ["Scaler ultrasónico", "Transductor, puntas, conector rápido y manguera"],
    ["Salivero / cuspidera", "Descarga, filtro de sólidos y manguera corrugada"],
    ["Sistema de succión", "Eyectores, válvulas de corte y trampas de saliva"],
    ["Sistema hidroneumático", "Reguladores de presión aire/agua y llaves de paso"],
    ["Block de control", "Válvulas on/off, pilotos y relés de la tarjeta"],
    ["Pedal neumático", "Membranas, resortes y manguera de mando"],
    ["Motores del sillón", "Motor de respaldo, base y finales de carrera"],
    ["Tapiz y estructura", "Tapicería, apoyacabeza, brazos y carenados"],
    ["Mangueras y conexiones", "Uniones, conectores rápidos, abrazaderas y T"],
    ["Llenado de vaso", "Botón, sensor y soporte del llena-vaso"],
  ]},
  { id: "autoclave", icon: Flame, nombre: "Autoclave", piezas: [
    ["Sello de puerta", "Empaquetadura: la falla más común, filtra vapor"],
    ["Sistema de cierre", "Bisagras, trabas y microswitch de seguridad"],
    ["Cámara y resistencia", "Calefactores, limpieza y sarro de la cámara"],
    ["Válvula de seguridad", "Alivio de presión: calibración obligatoria"],
    ["Sensores", "Presión y temperatura (PT100/termocuplas)"],
    ["Bomba de vacío", "Membranas y prefiltros del ciclo clase B"],
    ["Filtros", "Filtro bacteriológico de aire y filtro de agua"],
    ["Trampa de vapor", "Purgador y ciclos de secado"],
    ["Tarjeta electrónica", "Panel, display y programas de ciclo"],
    ["Validación", "Controles físicos, químicos y biológicos (MINSAL)"],
  ]},
  { id: "rayosx", icon: Radiation, nombre: "Rayos X (intra y extraoral)", piezas: [
    ["Tubo generador", "Cabezal, aceite dieléctrico y colimador"],
    ["Brazo pantógrafo", "Resortes, frenos y estabilidad del disparo"],
    ["Temporizador", "Precisión del tiempo de exposición"],
    ["Disparador", "Cable, gatillo remoto y pulsador"],
    ["Panorámico / CBCT", "Ejes de giro, sensores y calibración de imagen"],
    ["Sensores digitales", "Placas de fósforo, escáner y sensores RVG"],
    ["Control de calidad", "Verificación exigida por el ISP (DS 133)"],
    ["Blindaje y seguridad", "Delantales plomados y señalética radiológica"],
  ]},
  { id: "compresor", icon: Gauge, nombre: "Compresor dental", piezas: [
    ["Cabezal / pistones", "Anillos, empaquetaduras y válvulas de placa"],
    ["Motor eléctrico", "Rodamientos, capacitor y protector térmico"],
    ["Estanque", "Purga de condensado y revisión de corrosión"],
    ["Presostato", "Corte y arranque en presiones correctas"],
    ["Secador de aire", "Torres de secado y silica gel para aire seco"],
    ["Filtros", "Filtro de aspiración y filtros coalescentes"],
    ["Válvulas", "Retención, seguridad y purga automática"],
    ["Red de aire", "Mangueras, uniones y reguladores hacia los boxes"],
  ]},
  { id: "scaler", icon: Waves, nombre: "Scaler / Ultrasonido", piezas: [
    ["Transductor", "Pieza de mano y cristales piezoeléctricos"],
    ["Puntas / tips", "Desgaste, torque correcto y llave dinamométrica"],
    ["Conector rápido", "Orings y acople a la manguera"],
    ["Irrigación", "Manguera de agua, filtro y regulación de flujo"],
    ["Potencia", "Calibración de frecuencia y potencia de salida"],
  ]},
  { id: "lampara", icon: Zap, nombre: "Lámpara de fotocurado", piezas: [
    ["LED y óptica", "Intensidad (mW/cm²) medida con radiómetro"],
    ["Batería", "Autonomía y reemplazo de celdas"],
    ["Fibra / guía", "Limpieza y fractura de la guía de luz"],
    ["Soporte", "Soporte de brazo auxiliar y carga"],
  ]},
];

function FallbackParts() {
  const [tab, setTab] = useTabState(0);
  const eq = EQUIPOS_DETALLE[tab];
  return (
    <section className="py-20 md:py-28 bg-[#0B1020] relative overflow-hidden" data-testid="conoce-tu-equipo">
      <div className="pointer-events-none absolute inset-0 odf-grid opacity-[0.08]" />
      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Ingeniería de servicio</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
            Conoce tu equipo<br /><span className="text-[#0FB4B7]">pieza por pieza</span>
          </h2>
          <p className="mt-4 text-white/55">
            Cada componente es un punto de falla que dominamos — desde el motor hasta el último oring.
          </p>
        </Reveal>

        {/* selector de equipo */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {EQUIPOS_DETALLE.map((e, i) => (
            <button key={e.id} onClick={() => setTab(i)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all border ${
                i === tab
                  ? "bg-[#0FB4B7] text-[#0B1020] border-[#0FB4B7]"
                  : "bg-white/[0.04] text-white/60 border-white/10 hover:border-[#0FB4B7]/40 hover:text-white"
              }`}>
              <e.icon className="h-4 w-4" /> {e.nombre}
            </button>
          ))}
        </div>

        <div key={eq.id} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
          {eq.piezas.map(([t, d], i) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 hover:border-[#0FB4B7]/40 hover:bg-white/[0.07] transition-all">
              <div className="flex items-center gap-2.5">
                <span className="font-heading text-sm font-bold text-[#0FB4B7]/70 w-6">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[14.5px] font-semibold text-white">{t}</p>
              </div>
              <p className="mt-1.5 ml-[34px] text-[12.5px] text-white/50 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-white/40 text-sm mt-8">
          {eq.piezas.length} puntos de servicio en {eq.nombre.toLowerCase()} — y la lista sigue en terreno.
        </p>
      </div>
    </section>
  );
}

function FallbackPartsOld() {
  return (
    <section className="py-20 md:py-28 bg-[#0B1020] relative overflow-hidden" data-testid="sillon-parts">
      <div className="pointer-events-none absolute inset-0 odf-grid opacity-[0.08]" />
      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Ingeniería de servicio</p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
            Conocemos tu sillón<br /><span className="text-[#0FB4B7]">pieza por pieza</span>
          </h2>
          <p className="mt-4 text-white/55">Los 7 puntos críticos que revisamos en cada intervención.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {PARTS.map((pt, i) => (
            <Reveal key={pt.t} delay={i * 60}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5 hover:border-[#0FB4B7]/40 hover:bg-white/[0.07] transition-all">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0FB4B7]/15 flex-shrink-0">
                    <pt.icon className="h-5 w-5 text-[#0FB4B7]" />
                  </span>
                  <p className="text-[15px] font-semibold text-white">{pt.t}</p>
                  <span className="ml-auto font-heading text-xl font-bold text-white/15">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="mt-3 text-[13px] text-white/50 leading-relaxed">{pt.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SillonShowcase() {
  const manifest = useManifest();
  if (manifest === undefined) return <div className="bg-[#0B1020] h-24" />;
  if (manifest) return <OrbitScrub manifest={manifest} />;
  return <FallbackParts />;
}
